/**
 * RebaselineDialog.tsx — the controlled "replace golden with latest" action
 * (CJ 2026-07-30). A golden re-baseline is never silent: it requires a
 * written justification, and the superseded baseline is preserved in the
 * fixture's append-only golden history.
 */
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

const MIN_JUSTIFICATION = 10;

interface RebaselineDialogProps {
  open: boolean;
  fixtureName: string;
  busy: boolean;
  onClose: () => void;
  onConfirm: (justification: string) => void;
}

export const RebaselineDialog = ({
  open,
  fixtureName,
  busy,
  onClose,
  onConfirm,
}: RebaselineDialogProps) => {
  const [justification, setJustification] = useState('');
  const tooShort = justification.trim().length < MIN_JUSTIFICATION;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Re-baseline golden — {fixtureName}</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This re-runs the fixture and replaces every golden expectation and pinned column
          checksum with the application&rsquo;s CURRENT output. Only do this when the current
          behavior has been verified as correct (e.g. an approved formula change). The superseded
          baseline and your justification are kept permanently in the fixture&rsquo;s golden
          history.
        </Alert>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          label="Justification (required)"
          placeholder="Why are the new values correct? Reference the approved change…"
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
          disabled={busy}
        />
        <Typography variant="caption" color="text.secondary">
          Recorded with your name and timestamp in the append-only golden history.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          disabled={tooShort || busy}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
          onClick={() => onConfirm(justification.trim())}
        >
          Replace golden with latest
        </Button>
      </DialogActions>
    </Dialog>
  );
};
